// src/components/Documentos/Documentos.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box, Paper, Typography, Stack, Button, IconButton, Snackbar, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useAuth } from "../../context/AuthContext";

function iconForMime(mime) {
  if (!mime) return <InsertDriveFileIcon />;
  if (mime.startsWith("image/")) return <ImageIcon />;
  if (mime === "application/pdf") return <PictureAsPdfIcon />;
  if (mime.startsWith("text/")) return <DescriptionIcon />;
  return <InsertDriveFileIcon />;
}

// Normaliza snake_case -> camelCase (y admite ambos)
const normalizarDocumento = (d) => ({
  id: d.id,
  nombreArchivo: d.nombreArchivo ?? d.nombre_archivo ?? "",
  tipoContenido: d.tipoContenido ?? d.tipo_contenido ?? "",
  fechaSubida: d.fechaSubida ?? d.fecha_subida ?? null,
});

// Normaliza metadatos Page (Spring) con snake_case/camelCase
const getPageMeta = (data) => ({
  content: Array.isArray(data?.content) ? data.content : [],
  totalElements: data?.totalElements ?? data?.total_elements ?? 0,
  number: data?.number ?? data?.page ?? 0,
  size: data?.size ?? data?.page_size ?? 0,
});

export default function Documentos() {
  const API = process.env.REACT_APP_API_URL;
  const { usuario, token } = useAuth();
  const colegiadoId = usuario?.codigo;

  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort] = useState("fechaSubida,desc"); // si quieres selector de orden, exponlo en UI

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [confirm, setConfirm] = useState({ open: false, id: null, nombre: "" });

  const fileInputRef = useRef(null);

  const headers = useMemo(
    () => ({ Authorization: token ? `Bearer ${token}` : undefined }),
    [token]
  );

  const fetchDocs = async () => {
    if (!colegiadoId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API}/api/documentos/colegiado/${colegiadoId}`,
        { headers, params: { page, size: rowsPerPage, sort } }
      );
      const meta = getPageMeta(data);
      setDocs(meta.content.map(normalizarDocumento));
      setTotal(meta.totalElements);
      // opcional: sincronizar por si el server ajusta page/size
      // setPage(meta.number);
      // setRowsPerPage(meta.size || rowsPerPage);
    } catch (e) {
      console.error(e);
      setDocs([]);
      setTotal(0);
      setSnack({ open: true, msg: "No se pudieron cargar los documentos.", sev: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [colegiadoId, page, rowsPerPage, sort]);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFilesSelected = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length || !colegiadoId) return;

    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("colegiadoId", String(colegiadoId));
        await axios.post(`${API}/api/documentos/subir`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
      }
      setSnack({ open: true, msg: "Archivo(s) subido(s) correctamente.", sev: "success" });
      await fetchDocs();
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data && typeof e.response.data === "string"
          ? e.response.data
          : "Error subiendo archivos.";
      setSnack({ open: true, msg, sev: "error" });
    } finally {
      setUploading(false);
      ev.target.value = null;
    }
  };

  const handleDownload = async (id, nombre) => {
    try {
      const resp = await axios.get(`${API}/api/documentos/descargar/${id}`, {
        headers,
        responseType: "blob",
      });
      const cd = resp.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?$/i.exec(cd);
      const filename = match?.[1] || nombre || `documento_${id}`;

      const blob = new Blob([resp.data], {
        type: resp.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "No se pudo descargar el documento.", sev: "error" });
    }
  };

  const confirmDelete = (id, nombreArchivo) =>
    setConfirm({ open: true, id, nombre: nombreArchivo || "" });

  const handleDelete = async () => {
    const { id } = confirm;
    if (!id) return;
    try {
      await axios.delete(`${API}/api/documentos/${id}`, { headers });
      setSnack({ open: true, msg: "Documento eliminado.", sev: "success" });
      setConfirm({ open: false, id: null, nombre: "" });

      // Si hemos borrado el último de la página y no es la primera, retrocede página
      if (docs.length === 1 && page > 0) {
        setPage(page - 1); // el useEffect hará fetchDocs
      } else {
        await fetchDocs();
      }
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "No se pudo eliminar el documento.", sev: "error" });
    }
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={2}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Documentos Adjuntos</Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Recargar">
              <span>
                <IconButton onClick={fetchDocs} disabled={loading || uploading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFilesSelected}
              style={{ display: "none" }}
            />
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              disabled={uploading}
            >
              Subir documento(s)
            </Button>
          </Stack>
        </Stack>

        {(uploading || loading) && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Contenido</TableCell>
                <TableCell>Fecha subida</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id}>
                  <TableCell width={48}>{iconForMime(d.tipoContenido)}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>
                    {d.nombreArchivo || "-"}
                  </TableCell>
                  <TableCell>{d.tipoContenido || "-"}</TableCell>
                  <TableCell>
                    {d.fechaSubida ? new Date(d.fechaSubida).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Descargar">
                      <IconButton
                        onClick={() => handleDownload(d.id, d.nombreArchivo)}
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        onClick={() => confirmDelete(d.id, d.nombreArchivo)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No hay documentos subidos.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página"
        />
      </Paper>

      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, id: null, nombre: "" })}>
        <DialogTitle>Eliminar documento</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que deseas eliminar <strong>{confirm.nombre || "este documento"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, id: null, nombre: "" })}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
